import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import apiClient from '@/api/client';

interface InviteInfo {
  email: string;
  role: string;
}

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [validating, setValidating] = useState(true);
  const [tokenError, setTokenError] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setTokenError('Kein Einladungstoken gefunden.');
      setValidating(false);
      return;
    }
    apiClient.get<InviteInfo>(`/auth/invite/${token}`)
      .then(({ data }) => {
        setInviteInfo(data);
        setValidating(false);
      })
      .catch(() => {
        setTokenError('Der Einladungslink ist ungültig oder abgelaufen.');
        setValidating(false);
      });
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirm) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    setLoading(true);
    try {
      await apiClient.post('/auth/accept-invite', { token, newPassword });
      navigate('/login', { state: { message: 'Konto aktiviert! Du kannst dich jetzt anmelden.' } });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Fehler beim Aktivieren.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <span className="text-4xl">🌳</span>
          <h1 className="mt-3 text-2xl font-bold text-slate-900">Einladung annehmen</h1>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
          {validating ? (
            <div className="flex justify-center py-4">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
            </div>
          ) : tokenError ? (
            <div className="text-center">
              <p className="mb-4 text-sm text-red-600">{tokenError}</p>
              <Link to="/login" className="text-sm font-medium text-blue-600 hover:text-blue-700">
                Zur Anmeldung
              </Link>
            </div>
          ) : inviteInfo ? (
            <>
              <div className="mb-6 rounded-md bg-blue-50 p-4">
                <p className="text-sm text-blue-700">
                  Einladung für <strong>{inviteInfo.email}</strong>
                  <br />
                  <span className="text-blue-500">
                    Rolle: {inviteInfo.role === 'admin' ? 'Administrator' : 'Betrachter'}
                  </span>
                </p>
              </div>

              <p className="mb-5 text-sm text-slate-500">
                Lege dein Passwort fest, um dein Konto zu aktivieren.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="invitePw" className="mb-1 block text-sm font-medium text-slate-700">
                    Passwort (min. 8 Zeichen)
                  </label>
                  <input
                    id="invitePw"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    autoFocus
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>

                <div>
                  <label htmlFor="inviteConfirm" className="mb-1 block text-sm font-medium text-slate-700">
                    Passwort bestätigen
                  </label>
                  <input
                    id="inviteConfirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={8}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>

                {error && (
                  <p className="rounded-md bg-red-50 p-3 text-sm text-red-600">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-md bg-blue-600 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Aktiviere...' : 'Konto aktivieren'}
                </button>
              </form>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
